// ─────────────────────────────────────────────────────────────────────────────
// UNA SOLA AUTORIDAD MONETARIA DE PAQUETE · paquetes_cotizacion
//
// Misma forma que `cupoDePaquete.js`: una primitiva pura que recibe el catálogo ya cargado
// y decide. No hace peticiones, no cachea precios y no tiene ningún número dentro.
//
// ── LA POLÍTICA NO SE TOCA ────────────────────────────────────────────────
//
// La política comercial es la migración `402_el_precio_de_lanzamiento_se_decide_una_vez`,
// aprobada por el owner el 2026-09-24, y su encabezado declara qué NO es:
//
//   «Los nuevos diferenciales van del 17% al 25% y cada uno responde a una razón distinta.
//    Ninguno se calcula a partir de otro, y no se deja ninguna regla que un día alguien
//    pueda volver a aplicar en automático.»
//
// Es un PAR ABSOLUTO aprobado por producto y mercado. Por eso aquí no hay multiplicador, no
// hay derivación y no hay ningún precio escrito: se LEE la columna del mercado pedido.
//
// ── POR QUÉ EXISTE ────────────────────────────────────────────────────────
//
// `DEFAULT_PACKAGE_RULES` en `packages.js` mantiene precios ANTERIORES al Pricebook V1
// —1500 / 2900 / 4600 frente a los canónicos 1600 / 3000 / 4800— y cuatro consumidores
// monetarios los leían. Nada reconciliaba las dos tablas.
//
// ── LAS TRES REGLAS ──────────────────────────────────────────────────────
//
//   1. La autoridad es el `package_id`. El nombre sólo sirve para BUSCAR la fila, nunca
//      para dar un precio: `paquete_nombre` contradice al id vendido en 4 de 17 propuestas
//      aceptadas.
//   2. Falla CERRADO. Si no resuelve devuelve `null`, nunca 0 y nunca un número viejo.
//      «Un precio viejo es peor que ningún precio, porque el viejo parece verdad.»
//   3. El IVA no vive aquí. El precio es COMERCIAL y se guarda en NETO; el ×1,15 nacional
//      lo pone `grossForNational` al cobrar, que es su única fuente de verdad.
// ─────────────────────────────────────────────────────────────────────────────

export const PRECIO_AUTORIDAD = Object.freeze({
  /** El precio salió de la tabla canónica. */
  CANONICA: 'paquetes_cotizacion',
  /** No se pudo resolver el paquete. `precio` es null y hay que negarse a pintar o sumar. */
  SIN_RESOLVER: 'UNRESOLVED',
  /** El paquete está retirado: conserva precio archivado y NO autoriza venta nueva. */
  ARCHIVADO: 'ARCHIVED_NO_NEW_SALE',
});

/** Los tres corporativos que la 402 archivó. Conservan precio y no venden. */
export const IDS_ARCHIVADOS = Object.freeze(['corporate_essence', 'corporate_legacy', 'corporate_ascend']);

const texto = (v) => (v === null || v === undefined ? '' : String(v)).trim();

/** Sin tildes y en minúsculas, para buscar una fila por nombre sin depender de acentos. */
const clave = (v) => texto(v).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const MERCADO_NACIONAL = 'nacional';

/**
 * EL PRECIO CON LA FILA DEL CATÁLOGO EN LA MANO.
 *
 * @param fila     la fila de `paquetes_cotizacion`, o null si no se encontró
 * @param opciones { packageId, mercado } · `mercado` 'nacional' | 'internacional'
 *                 Sin mercado declarado se usa el internacional, que es el único que
 *                 siempre está declarado en la 402.
 */
export function precioDesdeLaFila(fila, opciones = {}) {
  const packageId = texto(opciones.packageId ?? fila?.id);
  const mercado = clave(opciones.mercado) === MERCADO_NACIONAL ? 'nacional' : 'internacional';
  const no = (autoridad) => ({ precio: null, autoridad, packageId, mercado, resuelto: false });

  if (!packageId || !fila) return no(PRECIO_AUTORIDAD.SIN_RESOLVER);

  const crudo = mercado === 'nacional'
    ? (fila.precio_nacional ?? fila.precioNacional ?? fila.precio)
    : (fila.precio_internacional ?? fila.precioInternacional ?? fila.precio);

  if (crudo === null || crudo === undefined || crudo === '') return no(PRECIO_AUTORIDAD.SIN_RESOLVER);
  const n = Number(crudo);
  if (!Number.isFinite(n) || n < 0) return no(PRECIO_AUTORIDAD.SIN_RESOLVER);

  return {
    precio: n,
    autoridad: IDS_ARCHIVADOS.includes(packageId) ? PRECIO_AUTORIDAD.ARCHIVADO : PRECIO_AUTORIDAD.CANONICA,
    packageId,
    mercado,
    resuelto: true,
    /** Un suelo no es un precio cerrado: `corporate_*` requiere discovery. */
    esSuelo: Boolean(fila.precio_es_suelo ?? fila.esSuelo ?? false),
    /** Un archivado conserva su precio y NO autoriza venta nueva. */
    autorizaVentaNueva: !IDS_ARCHIVADOS.includes(packageId),
  };
}

/**
 * EL PRECIO BUSCANDO LA FILA EN UN CATÁLOGO YA CARGADO.
 *
 * Prefiere `packageId`. `packageName` es SÓLO una clave de búsqueda para los consumidores
 * viejos que todavía manejan texto libre; nunca aporta un precio por sí mismo.
 */
export function precioCanonicoDelPaquete(catalogo, { packageId, packageName, mercado } = {}) {
  const lista = Array.isArray(catalogo) ? catalogo : [];
  const id = texto(packageId);
  let fila = id ? lista.find((p) => texto(p?.id) === id) || null : null;

  if (!fila && texto(packageName)) {
    const k = clave(packageName);
    fila = lista.find((p) => clave(p?.nombre) === k || clave(p?.name) === k || clave(p?.id) === k) || null;
  }
  return precioDesdeLaFila(fila, { packageId: id || texto(fila?.id), mercado });
}

/**
 * LO QUE UNA PANTALLA PUEDE PINTAR.
 *
 * Nunca 0, nunca NaN, nunca `undefined`. Si no resuelve, lo dice.
 * `ambito` distingue al staff -que puede ver un aviso explícito- del cliente, que no debe
 * ver jerga interna ni un número inventado.
 */
export function precioParaPintar(p, { ambito = 'staff' } = {}) {
  if (!p || !p.resuelto || p.precio === null) {
    return ambito === 'cliente'
      ? { texto: 'Valor confirmado por el estudio', precio: null, bloquea: true }
      : { texto: 'Sin precio canónico', precio: null, bloquea: true };
  }
  return { texto: '$' + p.precio.toLocaleString('en-US') + ' USD', precio: p.precio, bloquea: false };
}

/**
 * LO QUE UN TOTAL PUEDE SUMAR.
 *
 * Un precio sin resolver NO entra en un total: devuelve `{ suma: 0, omitido: true }` para que
 * el informe pueda decir cuántos dejó fuera en vez de sumar de menos en silencio. Sumar 0
 * como si fuera un ingreso de cero es exactamente el error que esto evita.
 */
export function aportacionAlTotal(p) {
  if (!p || !p.resuelto || p.precio === null) return { suma: 0, omitido: true };
  return { suma: p.precio, omitido: false };
}

// ─────────────────────────────────────────────────────────────────────────────
// EL REGISTRO · una cache de la autoridad, no una segunda autoridad
//
// Las cuatro funciones que consumian el precio son de modulo -viven fuera de React- y las
// pantallas que las llaman no cargan el catalogo. En vez de enhebrar el catalogo por
// veintitantos sitios, `AppContext` lo registra una vez cuando lo carga de la base.
//
// Tres invariantes que lo mantienen honesto:
//
//   1. NACE VACIO. Sin registrar, todo resuelve `UNRESOLVED`, que se niega. Un catalogo que
//      no llego no inventa un precio.
//   2. SOLO SE LLENA DESDE LA BASE. `registrarCatalogoCanonico` es lo unico que escribe, y
//      quien lo llama lee `paquetes_cotizacion`. No hay valores por defecto.
//   3. NO PERSISTE. No toca `localStorage`. Se pierde al recargar y se vuelve a pedir, que
//      es exactamente lo que se quiere: la autoridad esta en la base, no en el navegador.
// ─────────────────────────────────────────────────────────────────────────────

let CATALOGO_REGISTRADO = [];

/** Lo llama quien acaba de leer `paquetes_cotizacion`. Nadie mas. */
export function registrarCatalogoCanonico(filas) {
  CATALOGO_REGISTRADO = Array.isArray(filas) ? filas.filter(Boolean) : [];
  return CATALOGO_REGISTRADO.length;
}

/** Para pruebas y para que una pantalla pueda saber si ya hay autoridad cargada. */
export function catalogoCanonicoRegistrado() {
  return CATALOGO_REGISTRADO;
}

export function hayAutoridadDePrecio() {
  return CATALOGO_REGISTRADO.length > 0;
}

/** El precio canonico usando el catalogo registrado. Si no hay, se niega. */
export function precioCanonicoRegistrado(opciones = {}) {
  return precioCanonicoDelPaquete(CATALOGO_REGISTRADO, opciones);
}

/**
 * EL NUMERO O NADA, para los consumidores viejos que esperaban un numero.
 *
 * Devuelve `null`, nunca 0. Quien lo llame tiene que decidir que hacer con `null`, y eso es
 * el punto: antes recibia un precio de 2026-07 y no lo sabia.
 */
export function precioCanonicoONull(opciones = {}) {
  const r = precioCanonicoRegistrado(opciones);
  return r.resuelto ? r.precio : null;
}
